import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIsoLayoutEffect } from './useIsoLayoutEffect';

// The moving part of a carousel, kept apart from the drawing of one.
//
// The movement is the browser's own: the slides sit in a scroll container with `scroll-snap`, which is
// what buys a touch swipe, a trackpad flick and a keyboard PageDown for free. What the browser does not
// give is an endless run, so a looping carousel is drawn with a copy of the last slide before the first
// and a copy of the first after the last. The shopper always has a neighbour peeking on both sides, and
// once the run settles on a copy the scroll jumps — with no animation — onto the real slide it stands
// for. The jump cannot be seen because the two are the same picture.
//
// Without those copies the first slide had nothing to its left and the last nothing to its right, which
// read as the carousel having fallen off the edge of the page.

/** What a slideshow is asked to do, and how many slides it has to do it with. */
export type SlideshowPlan = { count: number; autoplay: boolean; holdSeconds: number; loop: boolean; pauseOnHover?: boolean };

/** What the drawing needs back: where it is, how to move, and the handlers that hold it still. */
export type SlideshowRun = {
  /** Which real slide is showing, whatever copy the scroll happens to be resting on. */
  index: number;
  /** Whether the run has moved yet — by autoplay, a dot or an arrow — rather than only settled where the page opened. */
  moved: boolean;
  paused: boolean;
  go: (to: number) => void;
  next: () => void;
  previous: () => void;
  viewport: React.RefObject<HTMLDivElement>;
  hold: { onMouseEnter: () => void; onMouseLeave: () => void; onFocus: (event: React.FocusEvent<HTMLDivElement>) => void; onBlur: () => void };
};

/** The shortest hold worth reading a slide in, which is also what a merchant's number is clamped up to. */
const MIN_HOLD = 2;

/** How long one slide takes to travel. Sleek's own slideshow runs 300ms and so does this. */
const SLIDE_MS = 300;

// Ease-out cubic: quick away, gentle on arrival, which is what a slide that snaps into place looks like.
const easeOut = (t: number): number => 1 - (1 - t) ** 3;

// Reports whether this visitor has asked their system for less movement, and keeps reporting it.
function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const read = () => setReduced(query.matches);
    read();
    query.addEventListener('change', read);
    return () => query.removeEventListener('change', read);
  }, []);
  return reduced;
}

/** Where one real slide sits among the drawn ones: the copies bracket them when the carousel loops. */
export const slotOf = (real: number, loop: boolean): number => (loop ? real + 1 : real);

/** Which real slide a drawn one stands for, so both copies report the slide they were made from. */
export const realOf = (slot: number, count: number, loop: boolean): number => {
  if (count <= 0) return 0;
  return loop ? (((slot - 1) % count) + count) % count : Math.min(Math.max(slot, 0), count - 1);
};

// Measures where a drawn slide has to sit for the viewport to hold it in the middle.
function offsetFor(node: HTMLDivElement, slot: number): number | null {
  const slide = node.children[slot] as HTMLElement | undefined;
  if (!slide) return null;
  return slide.offsetLeft - (node.clientWidth - slide.offsetWidth) / 2;
}

// Runs a carousel: which slide shows, and the four ways it changes.
export function useSlideshow(plan: SlideshowPlan): SlideshowRun {
  const looping = plan.loop && plan.count > 1;
  const [slot, setSlot] = useState(() => slotOf(0, looping));
  const [hovering, setHovering] = useState(false);
  const [focused, setFocused] = useState(false);
  const [moved, setMoved] = useState(false);
  const viewport = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
  const reduced = useReducedMotion();
  /** Which travel is the current one, so a finished tween cannot drag a newer one back. */
  const journey = useRef(0);

  /**
   * Puts the run on a drawn slide, sliding there unless the shopper asked for no movement.
   *
   * The tween is animated frame by frame, and then landed by a timer as well. A background tab, a
   * throttled renderer or a machine under load can starve `requestAnimationFrame` for whole seconds —
   * and a carousel that silently fails to arrive is worse than one that arrives without the slide.
   */
  const travel = useCallback((toSlot: number, animate: boolean) => {
    const node = viewport.current;
    const target = node ? offsetFor(node, toSlot) : null;
    if (!node || target === null) return;
    setSlot(toSlot);
    if (animate) setMoved(true);
    const mine = (journey.current += 1);
    if (!animate || reduced) { delete node.dataset.moving; node.scrollLeft = target; return; }
    // Native snap must wait until the tween lands, or it can snap each intermediate frame back.
    node.dataset.moving = 'true';
    const from = node.scrollLeft;
    const started = performance.now();
    const step = (now: number) => {
      if (journey.current !== mine) return;
      const through = Math.min(1, (now - started) / SLIDE_MS);
      node.scrollLeft = from + (target - from) * easeOut(through);
      if (through < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
    window.setTimeout(() => {
      if (journey.current !== mine) return;
      node.scrollLeft = target;
      delete node.dataset.moving;
    }, SLIDE_MS + 20);
  }, [reduced]);

  // Lands on the first real slide once the slides are laid out, past the copy that precedes it.
  //
  // Held in a ref and deliberately NOT a dependency: `travel` is rebuilt whenever the reduced-motion
  // query answers, and depending on it re-ran this effect mid-run — the carousel snapped straight back
  // to the first slide every time the shopper pressed a dot.
  const land = useRef(travel);
  land.current = travel;
  useIsoLayoutEffect(() => { land.current(slotOf(0, looping), false); }, [looping, plan.count]);

  // Touch and trackpad scrolling must update the controls too. Otherwise the next arrow starts
  // from the old slide and autoplay can unexpectedly send a shopper backwards.
  useEffect(() => {
    const node = viewport.current;
    if (!node) return;
    let timer: ReturnType<typeof setTimeout>;
    const read = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        let nearest = 0, distance = Infinity;
        Array.from(node.children).forEach((_, at) => {
          const offset = offsetFor(node, at);
          const delta = Math.abs((offset ?? 0) - node.scrollLeft);
          if (delta < distance) { nearest = at; distance = delta; }
        });
        setSlot(nearest);
      }, SLIDE_MS + 30);
    };
    const interrupt = () => { journey.current += 1; delete node.dataset.moving; };
    node.addEventListener('scroll', read, { passive: true });
    node.addEventListener('pointerdown', interrupt, { passive: true });
    node.addEventListener('wheel', interrupt, { passive: true });
    return () => {
      clearTimeout(timer);
      node.removeEventListener('scroll', read);
      node.removeEventListener('pointerdown', interrupt);
      node.removeEventListener('wheel', interrupt);
      journey.current += 1;
      delete node.dataset.moving;
    };
  }, [plan.count, looping]);

  // Steps off a copy onto the real slide it stands for, without animating — this is the loop's seam.
  useEffect(() => {
    if (!looping || (slot !== 0 && slot !== plan.count + 1)) return;
    const timer = window.setTimeout(
      () => travel(slot === 0 ? plan.count : 1, false),
      SLIDE_MS + 40
    );
    return () => window.clearTimeout(timer);
  }, [looping, slot, plan.count, travel]);

  const go = useCallback((real: number) => travel(slotOf(real, looping), true), [looping, travel]);

  // Moves one slide along, refusing to run past either end when the carousel does not loop.
  const step = useCallback((by: number) => {
    const next = slot + by;
    if (looping || (next >= 0 && next < plan.count)) travel(next, true);
  }, [looping, plan.count, slot, travel]);

  const next = useCallback(() => step(1), [step]);
  const previous = useCallback(() => step(-1), [step]);

  const still = Boolean(plan.pauseOnHover && hovering) || focused || reduced;
  useEffect(() => {
    if (!plan.autoplay || still || plan.count < 2) return;
    if (!looping && slot === plan.count - 1) return;
    const timer = window.setTimeout(next, Math.max(MIN_HOLD, plan.holdSeconds) * 1000);
    return () => window.clearTimeout(timer);
  }, [plan.autoplay, plan.count, plan.holdSeconds, looping, still, slot, next]);

  const hold = useMemo(() => ({
    onMouseEnter: () => setHovering(true),
    onMouseLeave: () => setHovering(false),
    onFocus: (event: React.FocusEvent<HTMLDivElement>) => setFocused(event.target.matches(':focus-visible')),
    onBlur: () => setFocused(false)
  }), []);

  return { index: realOf(slot, plan.count, looping), moved, paused: still, go, next, previous, viewport, hold };
}
